import { useEffect, useState } from "react";
import { BellRing } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMe } from "@/context/MeContext";
import { errMsg, eur, fmtDate, num, useApi } from "@/lib/api";
import { ClubCard } from "@/components/club/ClubCard";
import { StateBadge } from "@/components/club/StatusBadge";
import { BankTransferInfo } from "@/components/club/BankTransferInfo";
import { DeclareDialog } from "@/components/search/DeclareDialog";

function Orders({ orders }) {
  const [open, setOpen] = useState(null);
  if (!orders.length) return <p className="text-slate-500">Nessun ordine ancora.</p>;
  return (
    <div className="space-y-3">
      {orders.map((o) => (
        <div key={o.id} data-testid={`my-order-${o.id}`} className="rounded-2xl border border-white/10 bg-[#12141C] p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-white">{o.items.map((i) => i.name).join(", ")}</p>
              <p className="text-xs text-slate-500">{o.causale} · {o.type === "fatelo_voi" ? "Fatelo voi" : o.type === "status" ? "Acquisto status" : `Marketplace · ${o.partner_name}`} · {fmtDate(o.created_at)}</p>
            </div>
            <StateBadge status={o.status} testid={`my-order-status-${o.id}`} />
          </div>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-400">
            <span>Totale {eur(o.total)}</span>
            {o.points_used > 0 && <span>Punti usati {num(o.points_used)} (−{eur(o.points_discount)})</span>}
            <span>Da bonificare {eur(o.amount_due)}</span>
            {o.points_awarded > 0 && o.status !== "annullato" && <span className="text-amber-300">+{num(o.points_awarded)} pt</span>}
            {o.points_awarded > 0 && o.status === "annullato" && <span className="text-slate-500 line-through">+{num(o.points_awarded)} pt (stornati)</span>}
          </div>
          {o.status === "in_attesa_bonifico" && (
            <button data-testid={`my-order-bank-${o.id}`} onClick={() => setOpen(open === o.id ? null : o.id)} className="mt-3 text-sm text-amber-400 hover:underline">
              {open === o.id ? "Nascondi" : "Istruzioni bonifico"}
            </button>
          )}
          {open === o.id && <div className="mt-3"><BankTransferInfo order={o} /></div>}
        </div>
      ))}
    </div>
  );
}

function Ledger({ rows }) {
  if (!rows.length) return <p className="text-slate-500">Nessun movimento.</p>;
  return (
    <div className="divide-y divide-white/5 rounded-2xl border border-white/10 bg-[#12141C]">
      {rows.map((r) => (
        <div key={r.id} className="flex items-center justify-between p-4" data-testid={`ledger-row-${r.id}`}>
          <div>
            <p className="text-sm text-white">{r.description}</p>
            <p className="text-xs text-slate-500">{fmtDate(r.created_at)}</p>
          </div>
          <span className={`font-display font-semibold ${r.points >= 0 ? "text-emerald-400" : "text-red-400"}`}>{r.points > 0 ? "+" : ""}{num(r.points)} pt</span>
        </div>
      ))}
    </div>
  );
}

function Declarations({ rows, reload }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-3">
      <button data-testid="new-declaration-btn" onClick={() => setOpen(true)} className="rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-black">Dichiara un acquisto</button>
      {rows.map((d) => (
        <div key={d.id} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-white/10 bg-[#12141C] p-4" data-testid={`my-declaration-${d.id}`}>
          <div>
            <p className="text-sm text-white">{d.product_name} · {d.shop}</p>
            <p className="text-xs text-slate-500">Ordine {d.order_number} · {eur(d.amount)} · {fmtDate(d.created_at)}{d.note ? ` · ${d.note}` : ""}</p>
          </div>
          <div className="flex items-center gap-3">
            {d.points_awarded > 0 && <span className="text-sm text-amber-300">+{d.points_awarded} pt</span>}
            <StateBadge status={d.status} />
          </div>
        </div>
      ))}
      {open && <DeclareDialog onClose={() => setOpen(false)} onDone={reload} />}
    </div>
  );
}

function Watches({ rows, reload }) {
  const api = useApi();
  const remove = async (id) => {
    try {
      await api("delete", `/me/watches/${id}`);
      toast.success("Avviso rimosso");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!rows.length) return <p className="text-slate-500" data-testid="watches-empty">Nessun prodotto seguito. Dopo un confronto premi «Avvisami se il prezzo scende».</p>;
  return (
    <div className="space-y-3">
      {rows.map((w) => (
        <div key={w.id} data-testid={`watch-${w.id}`} className="flex flex-wrap items-center gap-4 rounded-2xl border border-white/10 bg-[#12141C] p-4">
          <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-xl bg-white">
            {w.image_url ? <img src={w.image_url} alt="" referrerPolicy="no-referrer" className="h-full w-full object-contain p-1" onError={(e) => (e.currentTarget.style.display = "none")} /> : <BellRing className="h-5 w-5 text-slate-400" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-white">{w.product_name}</p>
            <p className="text-xs text-slate-500">Seguito dal {fmtDate(w.created_at)} · prezzo iniziale {eur(w.baseline_total)} · ultimo controllo {w.last_checked_at ? fmtDate(w.last_checked_at) : "in attesa"}</p>
          </div>
          <div className="text-right">
            <p className="font-display text-lg font-bold text-white">{eur(w.last_total)}</p>
            <p className="text-xs text-slate-500">{w.last_shop}</p>
            {w.lowest_total < w.baseline_total && <p className="text-xs text-emerald-400">−{eur(w.baseline_total - w.lowest_total)}</p>}
          </div>
          <button data-testid={`watch-remove-${w.id}`} onClick={() => remove(w.id)} className="text-xs text-red-300 hover:underline">Rimuovi</button>
        </div>
      ))}
      <p className="text-xs text-slate-500">Controlliamo i prezzi ogni mattina e ti scriviamo via email (e SMS) quando il prezzo migliore scende.</p>
    </div>
  );
}

function Profile({ me, refresh }) {
  const api = useApi();
  const [form, setForm] = useState({ full_name: me.full_name || "", phone: me.phone || "" });
  const save = async () => {
    try {
      await api("put", "/me/profile", form);
      await refresh();
      toast.success("Profilo aggiornato");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="max-w-md space-y-3">
      <div><Label className="text-xs text-slate-400">Nome e cognome</Label><Input data-testid="profile-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1 bg-[#1C1F2C]" /></div>
      <div><Label className="text-xs text-slate-400">Cellulare per gli SMS</Label><Input data-testid="profile-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-[#1C1F2C]" /></div>
      <p className="text-xs text-slate-500">Email: {me.email}</p>
      <button data-testid="profile-save" onClick={save} className="rounded-full bg-amber-500 px-5 py-2 text-sm font-semibold text-black">Salva</button>
    </div>
  );
}

export default function Account() {
  const { me, refresh } = useMe();
  const api = useApi();
  const [data, setData] = useState({ orders: [], ledger: [], decls: [], watches: [] });
  const load = async () => {
    const [orders, ledger, decls, watches] = await Promise.all([api("get", "/me/orders"), api("get", "/me/ledger"), api("get", "/me/declarations"), api("get", "/me/watches")]);
    setData({ orders, ledger, decls, watches });
  };
  useEffect(() => {
    load().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!me) return <p className="py-24 text-center text-slate-500">Caricamento…</p>;
  return (
    <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6" data-testid="account-page">
      <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:items-center">
        <div>
          <p className="font-club text-xs text-amber-400">LA MIA AREA</p>
          <h1 className="mt-2 font-display text-4xl font-extrabold text-white sm:text-5xl">Ciao {me.full_name?.split(" ")[0] || ""}</h1>
          <p className="mt-3 text-slate-400">Punti {new Date().getFullYear() - 1}: {num(me.earned_last_year)} · Punti {new Date().getFullYear()}: {num(me.earned_this_year)}</p>
        </div>
        <ClubCard me={me} />
      </div>
      <Tabs defaultValue="orders" className="mt-12">
        <TabsList className="flex h-auto flex-wrap bg-[#12141C]">
          <TabsTrigger value="orders" data-testid="tab-orders">I miei ordini</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">Movimenti punti</TabsTrigger>
          <TabsTrigger value="decls" data-testid="tab-declarations">Acquisti dichiarati</TabsTrigger>
          <TabsTrigger value="watches" data-testid="tab-watches">Avvisi prezzo</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile">Profilo</TabsTrigger>
        </TabsList>
        <TabsContent value="orders" className="mt-6"><Orders orders={data.orders} /></TabsContent>
        <TabsContent value="ledger" className="mt-6"><Ledger rows={data.ledger} /></TabsContent>
        <TabsContent value="decls" className="mt-6"><Declarations rows={data.decls} reload={load} /></TabsContent>
        <TabsContent value="watches" className="mt-6"><Watches rows={data.watches} reload={load} /></TabsContent>
        <TabsContent value="profile" className="mt-6"><Profile me={me} refresh={refresh} /></TabsContent>
      </Tabs>
    </div>
  );
}
